import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from './ui/sonner';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_5a0445d2-18b0-46bc-93e5-7d48b61be08b/artifacts/gsnaomur_Logo%20Sin%20texto%20copy.png";

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const NewsletterForm = ({ t }) => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error(t('footer.newsletterInvalid'));
      return;
    }
    toast.success(t('footer.newsletterSuccess'));
    setEmail('');
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
      <div className="relative flex-1">
        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('footer.newsletterPlaceholder')}
          data-testid="newsletter-input"
          className="pl-9 h-11 bg-white/[0.03] border-[#1E2A4A] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus-visible:ring-[var(--accent-link)]"
        />
      </div>
      <Button
        type="submit"
        data-testid="newsletter-submit"
        className="h-11 px-5 bg-[var(--accent-link)] hover:bg-[var(--accent-link-hover)] text-white shrink-0"
      >
        {t('footer.newsletterButton')}
        <ArrowRight size={16} />
      </Button>
    </form>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const productLinks = [
    { path: '/', label: t('footer.aboutTivorao') },
    { path: '/experience', label: t('footer.scribaiExperience') },
    { path: '/product', label: t('footer.getScribai') },
  ];

  const legalLinks = [
    { path: '/privacy', label: t('footer.privacy') },
    { path: '/terms', label: t('footer.terms') },
    { path: '/shipping', label: t('footer.shipping') },
    { path: '/returns', label: t('footer.returns') },
    { path: '/contact', label: t('footer.contact') },
    { path: '/legal-notice', label: t('footer.legalNotice') },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0A1128] border-t border-[#1E2A4A]">
      <div className="container-wide py-16">
        {/* Newsletter */}
        <div className="glass-card rounded-2xl p-8 md:p-10 mb-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-semibold text-xl text-[var(--text-primary)] mb-1">{t('footer.newsletterTitle')}</h3>
            <p className="text-sm text-[var(--text-secondary)]">{t('footer.newsletterDesc')}</p>
          </div>
          <NewsletterForm t={t} />
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4" data-testid="footer-logo">
              <img src={LOGO_ICON} alt="Tivorao" className="h-8 w-auto" />
              <span className="font-medium text-lg text-[var(--text-primary)]">{t('footer.brand')}</span>
            </Link>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-medium text-[var(--text-primary)] mb-4">{t('footer.product')}</h4>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                    className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-medium text-[var(--text-primary)] mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                    className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Payment Methods */}
          <div>
            <h4 className="font-medium text-[var(--text-primary)] mb-4">{t('footer.securePayments')}</h4>
            <div className="flex flex-wrap gap-2">
              {['visa', 'american_express', 'apple_pay', 'diners_club', 'discover', 'google_pay', 'master', 'paypal', 'shopify_pay', 'klarna'].map((id) => (
                <div key={id} className="rounded-md overflow-hidden bg-white flex items-center justify-center" style={{ width: 50, height: 32 }}>
                  <img
                    src={`https://cdn.jsdelivr.net/gh/activemerchant/payment_icons/app/assets/images/payment_icons/${id}.svg`}
                    alt={id.replace(/_/g, ' ')}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#1E2A4A] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[var(--text-secondary)]">
            {t('footer.copyright')}
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            contact.tivorao@gmail.com
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
